import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { setInFlowCheckoutStep } from 'actions/CruiseActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import config from 'constants/config'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import HeaderContactOptionsMenu from 'components/App/Header/HeaderContactOptions/HeaderContactOptionsMenu'
import NavigationHeaderPhoneMenu from 'components/App/Header/HeaderMain/NavigationHeader/NavigationHeaderPhoneMenu'
import TextButton from 'components/Luxkit/Button/TextButton'
import IconButton from 'components/Luxkit/Button/IconButton'
import CheckoutSaveButtonGuard from 'tripPlanner/components/EntryPoints/CheckoutSaveButtonGuard'
import CheckoutSaveButton from 'tripPlanner/components/EntryPoints/CheckoutSaveButton'
import SwitchButton from 'components/Common/SwitchButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { RouterLocation } from 'connected-react-router'
import Caption from 'components/Luxkit/Typography/Caption'
import { getCruiseItems } from 'checkout/selectors/view/cruise'
import { getCruiseCurrentStep, CRUISE_CHECKOUT_STEPS, CRUISE_CHECKOUT_STEPS_V2 } from './steps/cruises'
import CheckoutSteps from './CheckoutSteps'
import CheckoutMobileSteps from './CheckoutMobileSteps'
import CheckoutProgressBar from './CheckoutProgressBar'
import { TOURS_CHECKOUT_STEPS } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/tours'
import { getCheckoutRouteFromURL } from 'checkout/lib/utils/misc/steps'
import {
  hasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade,
} from 'checkout/selectors/tourV2Selectors'

const DevModeToggleDesktop = styled(SwitchButton)`
  display: none;

  ${mediaQueryUp.largeDesktop} {
    display: inline-flex;
  }
`

const HeaderContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
`

const BackButtonDesktop = styled(TextButton)`
  display: none;

  ${mediaQueryUp.largeDesktop} {
    display: inline-flex;
  }
`

const BackButtonMobile = styled(IconButton)`
  ${mediaQueryUp.largeDesktop} {
    display: none;
  }
`

const PhoneNumberMobile = styled(HeaderContactOptionsMenu)`
  ${mediaQueryUp.largeDesktop} {
    display: none;
  }
`

const PhoneNumberDesktop = styled(NavigationHeaderPhoneMenu)`
  display: none;

  ${mediaQueryUp.largeDesktop} {
    display: flex;
  }
`

const ArrowIcon = styled(LineArrowRightIcon)`
  transform: rotate(180deg);
`

const LeftContent = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  height: 100%;
`

const RightContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row-reverse;

 ${mediaQueryUp.largeDesktop} {
    flex-direction: row;
    justify-content: flex-end;
  }
`

const StepsContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-end;
  align-items: center;
`

interface Props extends MappedProps {
  isDevToolsEnabled: boolean;
  onBack: () => void;
  onToggleDevTools: () => void;
}

function StepsHeaderCheckout(props: Props) {
  const dispatch = useAppDispatch()
  const {
    isDevToolsEnabled,
    onBack,
    onToggleDevTools,
    inFlowCheckoutStep,
    location,
    isCruise,
    isTour,
    isCruiseV2,
  } = props

  const currentCheckoutStep = useMemo(() => {
    if (isCruise) {
      return getCruiseCurrentStep(inFlowCheckoutStep, location.pathname)
    }

    if (isTour) {
      return getCheckoutRouteFromURL(location.pathname)
    }

    return ''
  }, [isTour, isCruise, location.pathname, inFlowCheckoutStep])

  const steps = useMemo(() => {
    if (isCruiseV2) {
      return CRUISE_CHECKOUT_STEPS_V2
    }

    if (isCruise) {
      return CRUISE_CHECKOUT_STEPS
    }

    if (isTour) {
      return TOURS_CHECKOUT_STEPS
    }

    return []
  }, [isTour, isCruise, isCruiseV2])

  const currentStepIndex = useMemo(() => steps.findIndex((item) => (
    item.step === currentCheckoutStep ||
    item.subSteps?.some((subStep) => subStep.step === currentCheckoutStep,
    ))), [steps, currentCheckoutStep])

  const handlerBack = useCallback(() => {
    const flatSteps = steps.flatMap((step) => step.subSteps?.length ? step.subSteps : step)
    const stepIndex = flatSteps.findIndex((step) => step.step === currentCheckoutStep)
    const currentStep = flatSteps[stepIndex]
    const prevStep = flatSteps[stepIndex - 1]

    if (prevStep && !prevStep.isRoute) {
      dispatch(setInFlowCheckoutStep(prevStep.step))
    }

    if (!prevStep || prevStep.isRoute || currentStep.isRoute) {
      onBack()
    }
  }, [onBack, steps, dispatch, currentCheckoutStep])

  return <HeaderContainer>
    <LeftContent>
      <BackButtonDesktop kind="tertiary" variant="dark" outdent="left" size="large" startIcon={<ArrowIcon size="XS" />} onClick={handlerBack}>
        Back
      </BackButtonDesktop>
      {config.UNIVERSAL_CHECKOUT_DEV_STATE_ENABLED && <DevModeToggleDesktop label="<dev />" toggled={isDevToolsEnabled} onToggle={onToggleDevTools} />}
      <BackButtonMobile kind="tertiary" variant="dark" size="large" onClick={handlerBack}>
        <ArrowIcon size="XS" />
      </BackButtonMobile>
    </LeftContent>
    <StepsContent>
      <Caption variant="medium" colour="neutral-three">
        Step {currentStepIndex + 1} of {steps.length}
      </Caption>

      <CheckoutMobileSteps
        steps={steps}
        currentStepIndex={currentStepIndex}
      />

      <CheckoutSteps
        steps={steps}
        currentStepIndex={currentStepIndex}
      />

      <CheckoutProgressBar
        currentStepIndex={currentStepIndex}
        maxSteps={steps.length}
      />
    </StepsContent>
    <RightContent>
      <PhoneNumberDesktop />
      <PhoneNumberMobile />
      <CheckoutSaveButtonGuard>
        <CheckoutSaveButton />
      </CheckoutSaveButtonGuard>
    </RightContent>
  </HeaderContainer>
}

interface MappedProps {
  inFlowCheckoutStep: string | null;
  location: RouterLocation<unknown>;
  isCruise: boolean;
  isTour: boolean;
  isCruiseV2: boolean;
}

const mapStateToProps = (state: App.State): MappedProps => {
  return {
    inFlowCheckoutStep: state.cruise.inFlowCheckoutStep,
    location: state.router.location,
    isCruise: getCruiseItems(state).length > 0,
    isCruiseV2: getCruiseItems(state)[0]?.version === 'v2',
    isTour: hasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade(state) && config.TOUR_V2_NEW_CHECKOUT_ENABLED,
  }
}

export default connect(mapStateToProps)(StepsHeaderCheckout)
